import React, { useEffect, useState } from 'react';
import { ArrowRight, Award, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { usePortfolio } from '../context/PortfolioContext';

const Hero = () => {
  const { hero, about, loading } = usePortfolio();
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setOffsetY(window.pageYOffset);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading || !hero || !about) {
    return (
      <section className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </section>
    );
  }

  return (
    <section id="hero" className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 py-20 md:py-32">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          {/* Left Content */}
          <div
            className="space-y-8"
            style={{
              transform: `translateY(${offsetY * 0.15}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full text-sm font-medium animate-fade-in-up">
                <Award size={16} />
                Curious, Hungry & Brave
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-tight animate-fade-in-up animation-delay-200">
                {hero.name}
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 font-medium animate-fade-in-up animation-delay-400">
                {hero.title}
              </p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed max-w-xl animate-fade-in-up animation-delay-600">
              {hero.description}
            </p>

            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-800">
              <Button
                onClick={() => scrollToSection('ventures')}
                size="lg"
                className="bg-gray-900 text-white hover:bg-gray-800 group"
              >
                Explore Ventures
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Button>
              <Button
                onClick={() => scrollToSection('contact')}
                size="lg"
                variant="outline"
                className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
              >
                Get in Touch
              </Button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-gray-200">
              {about.stats.map((stat, index) => (
                <div
                  key={index}
                  className="space-y-1 animate-scale-in"
                  style={{ animationDelay: `${1000 + index * 100}ms` }}
                >
                  <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div
            className="relative"
            style={{
              transform: `translateY(${offsetY * -0.1}px)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 shadow-2xl animate-fade-in-right animation-delay-400">
              <img
                src={hero.image}
                alt={hero.name}
                className="w-full h-full object-cover"
              />
            </div>
            {/* Floating Card */}
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-gray-100 max-w-xs animate-fade-in-up animation-delay-1000">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gray-900 rounded-lg">
                  <Building2 className="text-white" size={20} />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Bondstein Technologies</div>
                  <div className="text-sm text-gray-600">Co-Founder & CEO</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;